import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useEmulation } from '../contexts/EmulationContext'
import { creditsService } from '../services/credits'
import './DemoBanner.scss'

const DemoBanner = () => {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const { emulationSession } = useEmulation()

  // Don't render for super admins, platform admins, regular users, or when emulating
  const isSuperAdmin = user?.isSuperAdmin === true
  const isPlatformAdmin = user?.isPlatformAdmin === true
  const isAdmin = user?.role === 'admin' || user?.isTenantAdmin
  const isEmulating = !!emulationSession && emulationSession.status === 'active'

  // Fetch credit status to determine if tenant is on a demo plan
  // Only tenant admins need to see the demo banner
  const { data: creditStatus } = useQuery({
    queryKey: ['my-credit-status'],
    queryFn: () => creditsService.getMyCreditStatus(),
    staleTime: 60000,
    enabled: isAdmin && !isSuperAdmin && !isPlatformAdmin && !isEmulating,
  })

  // Don't render if not a tenant admin or is super/platform admin
  if (!isAdmin || isSuperAdmin || isPlatformAdmin || isEmulating) {
    return null
  }

  if (!creditStatus || creditStatus.type !== 'demo') {
    return null
  }

  // Calculate days remaining from demoExpiresAt
  const daysRemaining = creditStatus.daysRemaining ?? 0

  // Determine banner variant
  let variant = 'info'
  if (daysRemaining <= 0) {
    variant = 'danger'
  } else if (daysRemaining <= 7) {
    variant = 'warning'
  }

  return (
    <div className={`demo-banner demo-banner--${variant}`}>
      <span className="demo-banner__text">
        {variant === 'danger' && (
          t('demoBanner.expired', 'Your trial has expired.')
        )}
        {variant === 'warning' && (
          t('demoBanner.expiringSoon', 'Your trial expires in {{days}} days.', { days: daysRemaining })
        )}
        {variant === 'info' && (
          t('demoBanner.active', "You're on a free trial. {{days}} days remaining.", { days: daysRemaining })
        )}
      </span>
      <Link
        to="/administration?tab=billing"
        className="demo-banner__cta"
      >
        {variant === 'danger'
          ? t('demoBanner.upgradeToContinue', 'Upgrade to continue')
          : variant === 'warning'
            ? t('demoBanner.upgradeNow', 'Upgrade now')
            : t('demoBanner.upgrade', 'Upgrade')
        }
      </Link>
    </div>
  )
}

export default DemoBanner
